import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    CircularProgress,
    FormHelperText
} from '@mui/material';
import CryptoJS from "crypto-js";
import {ApiDetails, KEY} from "../../dummyData";

const CreateTicketDialog = ({ open, onClose, onTicketCreated, showAlert }) => {
    const [newTicket, setNewTicket] = useState({
        description: '',
        problem_type: '',
        problem: '',
        priority: 'Medium',
        status: 'OPEN',
        tenant_id: '',
        property_id: '',
        house_number: '',
        is_internal: false,
        initial_comment: ''
    });
    const [loading, setLoading] = useState(false);
    const [problemTypes, setProblemTypes] = useState([]);
    const [problems, setProblems] = useState([]);
    const [properties, setProperties] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            fetchProblemTypes();
            fetchProperties();
            resetForm();
        }
    }, [open]);

    const resetForm = () => {
        setNewTicket({
            description: '',
            problem_type: '',
            problem: '',
            priority: 'Medium',
            status: 'OPEN',
            tenant_id: '',
            property_id: '',
            house_number: '',
            is_internal: false,
            initial_comment: ''
        });
        setErrors({});
    };

    const getLandlordId = () => {
        try {
            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error getting landlord ID:", error);
            return null;
        }
    };

    const fetchProblemTypes = async () => {
        try {
            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/problem-types", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                setProblemTypes(data);
            } else {
                showAlert("Failed to load problem types", "error");
            }
        } catch (error) {
            console.error("Error fetching problem types:", error);
            showAlert("Error fetching problem types", "error");
        }
    };

    const fetchProblems = async (problemTypeId) => {
        if (!problemTypeId) return;

        try {
            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/problems", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId,
                    problem_type_id: problemTypeId
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                setProblems(data);
            } else {
                showAlert("Failed to load categories", "error");
            }
        } catch (error) {
            console.error("Error fetching problems:", error);
            showAlert("Error fetching categories", "error");
        }
    };

    const fetchProperties = async () => {
        try {
            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/get_property_summary", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                setProperties(data);
            } else {
                showAlert("Failed to load properties", "error");
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
            showAlert("Error fetching properties", "error");
        }
    };

    const fetchTenantsByProperty = async (propertyId) => {
        if (!propertyId) return;

        try {
            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/tenants/by_property", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId,
                    property_id: propertyId
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                setTenants(data);
            } else {
                showAlert("Failed to load tenants", "error");
            }
        } catch (error) {
            console.error("Error fetching tenants:", error);
            showAlert("Error fetching tenants", "error");
        }
    };

    const handlePropertyChange = (e) => {
        const propertyId = e.target.value;
        setNewTicket({...newTicket, property_id: propertyId, tenant_id: '', house_number: ''});
        fetchTenantsByProperty(propertyId);
    };

    const handleProblemTypeChange = (e) => {
        const problemTypeId = e.target.value;
        setNewTicket({...newTicket, problem_type: problemTypeId, problem: ''});
        fetchProblems(problemTypeId);

        // Clear error for the field
        if (errors.problem_type) {
            setErrors({
                ...errors,
                problem_type: null
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewTicket({...newTicket, [name]: value});

        // Clear error for the field
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: null
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!newTicket.description.trim()) {
            newErrors.description = 'Description is required';
        }

        if (!newTicket.problem_type) {
            newErrors.problem_type = 'Problem type is required';
        }

        if (!newTicket.problem) {
            newErrors.problem = 'Problem category is required';
        }

        if (!newTicket.priority) {
            newErrors.priority = 'Priority is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const createTicket = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);

            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                setLoading(false);
                return;
            }

            const ticketData = {
                ...newTicket,
                landlord_id: landlordId
            };

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/create", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify(ticketData)
            });

            if (response.status === 200) {
                const result = await response.json();
                showAlert("Ticket created successfully", "success");
                onTicketCreated?.(result);
                handleClose();
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error creating ticket:", error);
            showAlert("Failed to create ticket", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '10px'
                }
            }}
        >
            <DialogTitle sx={{ py: 2, px: 3, bgcolor: '#F9FAFB' }}>
                Create New Maintenance Ticket
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description *"
                            name="description"
                            value={newTicket.description}
                            onChange={handleInputChange}
                            multiline
                            rows={3}
                            placeholder="Describe the maintenance issue in detail..."
                            error={!!errors.description}
                            helperText={errors.description}
                            sx={{ mb: 2 }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors.problem_type} sx={{ mb: 2 }}>
                            <InputLabel>Problem Type *</InputLabel>
                            <Select
                                value={newTicket.problem_type}
                                label="Problem Type *"
                                onChange={handleProblemTypeChange}
                            >
                                <MenuItem value="">Select a problem type</MenuItem>
                                {problemTypes.map(type => (
                                    <MenuItem key={type.id} value={type.id}>
                                        {type.problem_type}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.problem_type && (
                                <FormHelperText>{errors.problem_type}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth disabled={!newTicket.problem_type} error={!!errors.problem} sx={{ mb: 2 }}>
                            <InputLabel>Category *</InputLabel>
                            <Select
                                value={newTicket.problem}
                                label="Category *"
                                name="problem"
                                onChange={handleInputChange}
                            >
                                <MenuItem value="">Select a category</MenuItem>
                                {problems.map(problem => (
                                    <MenuItem key={problem.id} value={problem.id}>
                                        {problem.problem}
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.problem && (
                                <FormHelperText>{errors.problem}</FormHelperText>
                            )}
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Priority *</InputLabel>
                            <Select
                                value={newTicket.priority}
                                label="Priority *"
                                name="priority"
                                onChange={handleInputChange}
                            >
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="Low">Low</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Property</InputLabel>
                            <Select
                                value={newTicket.property_id}
                                label="Property"
                                onChange={handlePropertyChange}
                            >
                                <MenuItem value="">None</MenuItem>
                                {properties.map(property => (
                                    <MenuItem key={property.property_id} value={property.property_id}>
                                        {property.property_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth disabled={!newTicket.property_id} sx={{ mb: 2 }}>
                            <InputLabel>Tenant</InputLabel>
                            <Select
                                value={newTicket.tenant_id}
                                label="Tenant"
                                name="tenant_id"
                                onChange={handleInputChange}
                            >
                                <MenuItem value="">None</MenuItem>
                                {tenants.map(tenant => (
                                    <MenuItem
                                        key={tenant.tenant_id}
                                        value={tenant.tenant_id}
                                        onClick={() => setNewTicket({
                                            ...newTicket,
                                            tenant_id: tenant.tenant_id,
                                            house_number: tenant.house_number
                                        })}
                                    >
                                        {tenant.first_name} {tenant.last_name} ({tenant.house_number})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="House/Unit Number"
                            name="house_number"
                            value={newTicket.house_number}
                            onChange={handleInputChange}
                            disabled={!!newTicket.tenant_id}
                            sx={{ mb: 2 }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Initial Comment (Optional)"
                            name="initial_comment"
                            value={newTicket.initial_comment}
                            onChange={handleInputChange}
                            multiline
                            rows={2}
                            placeholder="Add any additional notes or comments..."
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, bgcolor: '#F9FAFB' }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{ borderRadius: '8px' }}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={createTicket}
                    disabled={loading}
                    sx={{ borderRadius: '8px' }}
                >
                    {loading ? <CircularProgress size={24} /> : "Create Ticket"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTicketDialog;